import { useEffect, useMemo } from 'react';
import { availableActions, availableMoves, currentNode } from '@core/engine';
import { AUTOSAVE_SLOT } from '@core/persistence';
import { Board } from '@ui/components/Board';
import { Hud } from '@ui/components/Hud';
import { EventCard, OutcomeCard, DeltaList } from '@ui/components/Cards';
import { ActionBar } from '@ui/components/ActionBar';
import { Panel } from '@ui/components/Panels';
import { TitleScreen, SetupScreen } from '@ui/components/Screens';
import { EndingScreen } from '@ui/components/Ending';
import { actions, useApp } from '@ui/store';
import './App.css';

export function App() {
  const app = useApp();
  const session = app.session;

  const hasAutosave = useMemo(() => app.saves.some((s) => s.slot === AUTOSAVE_SLOT), [app.saves]);

  /* Keyboard: number keys pick a choice, Enter advances, Escape closes. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!session) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

      if (app.panel && event.key === 'Escape') {
        actions.closePanel();
        return;
      }
      if (session.phase === 'outcome' && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        actions.acknowledge();
        return;
      }
      if (session.phase === 'event' && session.currentEvent) {
        const index = Number(event.key);
        if (!Number.isNaN(index) && index >= 1) {
          const option = session.currentEvent.choices.filter((c) => c.enabled)[index - 1];
          if (option) actions.choose(option.choice.id);
        }
      }
      if (session.phase === 'board') {
        const moves = availableMoves(session);
        const index = Number(event.key);
        if (!Number.isNaN(index) && index >= 1 && moves[index - 1]) {
          actions.move(moves[index - 1]?.id ?? '');
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session, app.panel]);

  // Checked before the `!session` fallback: on a brand-new game there is no
  // session yet, and the title branch would otherwise swallow the setup screen.
  if (app.screen === 'setup') {
    return (
      <>
        <SetupScreen onStart={actions.startNewLife} onBack={actions.goTitle} />
        <Notice notice={app.notice} />
      </>
    );
  }

  if (app.screen === 'title' || !session) {
    return (
      <>
        <TitleScreen
          hasAutosave={hasAutosave}
          onNew={actions.goSetup}
          onContinue={() => void actions.continueLatest()}
          onSaves={() => {
            if (app.saves.length === 0) {
              actions.goSetup();
              return;
            }
            void actions.loadFrom(app.saves[0]?.slot ?? AUTOSAVE_SLOT);
          }}
        />
        <Notice notice={app.notice} />
      </>
    );
  }

  if (app.screen === 'ending' && session.state.ending) {
    return (
      <>
        <EndingScreen
          state={session.state}
          report={session.state.ending}
          onNewLife={actions.goSetup}
          onSameSeed={() =>
            actions.startNewLife({
              seed: session.state.rng.seed,
              name: session.state.identity.name,
              originId: session.state.identity.originId,
              motivationId: session.state.identity.motivationId,
            })
          }
          onTitle={actions.goTitle}
        />
        <Notice notice={app.notice} />
      </>
    );
  }

  const node = currentNode(session);
  const moves = availableMoves(session);
  const offered = availableActions(session);
  const state = session.state;

  return (
    <div className="app">
      <Hud state={state} showNumbers={app.settings.showNumbers} onOpenPanel={actions.openPanel} />

      <Board
        board={state.board}
        currentId={state.nodeId}
        visited={state.visitedNodes}
        available={moves}
        age={state.age}
        onSelect={actions.move}
        interactive={session.phase === 'board'}
      />

      {node && (
        <ActionBar
          node={node}
          actions={offered}
          actionUsed={session.actionUsed}
          canMove={session.phase === 'board' && moves.length > 0}
          onAct={actions.act}
          onConclude={actions.concludeLife}
        />
      )}

      {session.phase === 'board' && session.lastTurn && session.lastTurn.deltas.length > 0 && (
        <div className="app__turn" role="status" key={session.state.age + ':' + session.state.nodeId}>
          {session.lastTurn.yearsAdvanced > 0 && (
            <span className="app__turnYears">
              {session.lastTurn.yearsAdvanced} year{session.lastTurn.yearsAdvanced === 1 ? '' : 's'} passed
            </span>
          )}
          <DeltaList deltas={session.lastTurn.deltas} limit={5} />
        </div>
      )}

      {(session.phase === 'event' || session.phase === 'outcome') && (
        <div className="app__modal" role="dialog" aria-modal="true">
          <div className="app__modalScrim" aria-hidden="true" />
          <div className="app__modalBody">
            {session.phase === 'event' && session.currentEvent && (
              <EventCard presented={session.currentEvent} onChoose={actions.choose} />
            )}
            {session.phase === 'outcome' && session.lastResult && (
              <OutcomeCard
                title={session.lastResult.eventTitle}
                result={session.lastResult}
                onContinue={actions.acknowledge}
              />
            )}
          </div>
        </div>
      )}

      {app.panel && (
        <Panel
          panel={app.panel}
          state={state}
          settings={app.settings}
          saves={app.saves}
          onClose={actions.closePanel}
          onUpdateSettings={actions.updateSettings}
          onSave={(slot) => void actions.saveTo(slot)}
          onLoad={(slot) => void actions.loadFrom(slot)}
          onDelete={(slot) => void actions.deleteSave(slot)}
        />
      )}

      <Notice notice={app.notice} />
    </div>
  );
}

function Notice({ notice }: { notice: { id: number; text: string; tone: 'info' | 'warn' } | null }) {
  if (!notice) return null;
  return (
    <div className={`notice notice--${notice.tone}`} role="status" key={notice.id}>
      {notice.text}
    </div>
  );
}
